import type { ConsentManagerConfig, Translation } from '@/core/types';
import { FloatingButtonStyles } from './floating-button-styles';

export class FloatingButton {
  private element: HTMLElement | null = null;
  private shadowRoot: ShadowRoot | null = null;
  private config: ConsentManagerConfig;
  private translation: Translation;
  private onClick: () => void;
  private isVisible = false;

  constructor(
    config: ConsentManagerConfig,
    translation: Translation,
    onClick: () => void
  ) {
    this.config = config;
    this.translation = translation;
    this.onClick = onClick;
  }

  public show(): void {
    if (this.isVisible || this.config.showFloatingButton === false) return;

    this.createElement();
    this.attachToDOM();
    this.isVisible = true;

    // Animate in with a slight delay for better UX
    setTimeout(() => {
      this.element?.classList.add('cmp-floating-button--visible');
    }, 500);
  }

  public hide(): void {
    if (!this.isVisible || !this.element) return;

    this.element.classList.remove('cmp-floating-button--visible');

    setTimeout(() => {
      this.removeFromDOM();
      this.isVisible = false;
    }, 300);
  }

  public isShowing(): boolean {
    return this.isVisible;
  }

  private createElement(): void {
    this.element = document.createElement('div');
    this.element.className = 'cmp-floating-button';
    this.element.setAttribute('role', 'button');
    this.element.setAttribute(
      'aria-label',
      this.translation.footer.privacySettings
    );
    this.element.setAttribute('tabindex', '0');

    this.shadowRoot = this.element.attachShadow({ mode: 'closed' });

    const styles = document.createElement('style');
    styles.textContent = FloatingButtonStyles.getStyles(this.config);

    const content = document.createElement('div');
    content.className = 'cmp-floating-button__container';
    content.innerHTML = this.getHTML();

    this.shadowRoot.appendChild(styles);
    this.shadowRoot.appendChild(content);

    this.attachEventListeners();
  }

  private getHTML(): string {
    return `
      <button 
        type="button" 
        class="cmp-floating-button__button"
        aria-label="${this.escapeHTML(this.translation.footer.privacySettings)}"
        title="${this.escapeHTML(this.translation.footer.privacySettings)}"
      >
        <div class="cmp-floating-button__icon">
          <!-- Default privacy icon - will be replaced with brand icon later -->
          <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 1L3 5V11C3 16.55 6.84 21.74 12 23C17.16 21.74 21 16.55 21 11V5L12 1M12 7C13.4 7 14.8 8.6 14.8 10V11.5C15.4 11.5 16 12.4 16 13V16C16 17.4 15.4 18 14.8 18H9.2C8.6 18 8 17.4 8 16V13C8 12.4 8.6 11.5 9.2 11.5V10C9.2 8.6 10.6 7 12 7M12 8.2C11.2 8.2 10.5 8.7 10.5 10V11.5H13.5V10C13.5 8.7 12.8 8.2 12 8.2Z"/>
          </svg>
        </div>
        <span class="cmp-floating-button__text">
          ${this.escapeHTML(this.translation.footer.privacySettings)}
        </span>
      </button>
    `;
  }

  private attachEventListeners(): void {
    if (!this.shadowRoot) return;

    const button = this.shadowRoot.querySelector(
      '.cmp-floating-button__button'
    );
    if (!button) return;

    button.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      this.onClick();
    });

    // Handle keyboard navigation
    button.addEventListener('keydown', (event) => {
      const keyboardEvent = event as KeyboardEvent;
      if (keyboardEvent.key === 'Enter' || keyboardEvent.key === ' ') {
        event.preventDefault();
        this.onClick();
      }
    });

    // Add hover animation
    button.addEventListener('mouseenter', () => {
      button.classList.add('cmp-floating-button__button--hover');
    });

    button.addEventListener('mouseleave', () => {
      button.classList.remove('cmp-floating-button__button--hover');
    });
  }

  private attachToDOM(): void {
    if (!this.element) return;

    // Remove any existing floating button
    const existingButton = document.querySelector('.cmp-floating-button');
    if (existingButton) {
      existingButton.remove();
    }

    document.body.appendChild(this.element);
  }

  private removeFromDOM(): void {
    if (this.element && this.element.parentNode) {
      this.element.parentNode.removeChild(this.element);
    }
    this.element = null;
    this.shadowRoot = null;
  }

  private escapeHTML(text: string): string {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  public updateTranslation(translation: Translation): void {
    this.translation = translation;
    if (this.isVisible && this.shadowRoot) {
      const button = this.shadowRoot.querySelector(
        '.cmp-floating-button__button'
      );
      const text = this.shadowRoot.querySelector('.cmp-floating-button__text');
      if (button && text) {
        button.setAttribute(
          'aria-label',
          this.translation.footer.privacySettings
        );
        button.setAttribute('title', this.translation.footer.privacySettings);
        text.textContent = this.translation.footer.privacySettings;
      }
    }
  }

  public destroy(): void {
    this.hide();
  }
}
